/**
 * PrivacyPage  — /privacy
 *
 * MomentSent LLC Privacy Policy.
 * Effective date: May 15, 2026 (LLC formation date).
 * Governing law: State of North Carolina.
 *
 * Services referenced:
 *   Supabase (auth + database), Stripe (billing), Thankster (folded-card printing
 *   + mailing, US and international), Twilio (SMS), SendGrid (email).
 */

import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';

export default function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" effectiveDate="May 15, 2026">
      <PageSEO
        title="Privacy Policy | MomentSent"
        description="Read the MomentSent privacy policy to understand how we collect, use, and protect your personal information."
        canonical="https://momentsent.com/privacy"
      />

      <Section title="1. Who We Are">
        <p>
          MomentSent LLC ("MomentSent", "we", "us", or "our") is a North Carolina limited
          liability company headquartered in Hubert, NC. We operate the MomentSent web
          application at momentsent.com (the "Service"), which allows subscribers to schedule
          and send physical printed cards to their contacts.
        </p>
        <p>
          Questions about this policy may be directed to{' '}
          <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
            hello@momentsent.com
          </a>.
        </p>
      </Section>

      <Section title="2. Information We Collect">
        <SubSection title="2.1 Information you provide directly">
          <ul>
            <li><strong>Account data:</strong> First name, last name, and email address when you create an account. Passwords are hashed using bcrypt and are never stored in readable form.</li>
            <li><strong>Profile data:</strong> Phone number, which we use exclusively to send SMS card reminders and delivery notifications. If you enable push notifications in your browser, we store a push notification token to send you in-app alerts about upcoming card sends and delivery confirmations.</li>
            <li><strong>Contact data:</strong> Names and mailing addresses of the people to whom you send cards. This information is used solely to print and mail cards on your behalf.</li>
            <li><strong>Card content:</strong> Messages, occasion notes, and personalization details you enter for each card.</li>
            <li><strong>Payment data:</strong> Billing details collected and stored by Stripe, our payment processor. MomentSent never stores raw card numbers, CVVs, or full bank account information.</li>
            <li><strong>Consent records:</strong> Timestamps and IP address recorded when you agree to our Terms of Service and SMS consent at signup, as required by law.</li>
          </ul>
        </SubSection>

        <SubSection title="2.2 Information collected automatically">
          <ul>
            <li><strong>Log data:</strong> IP addresses, browser type, pages visited, and timestamps when you interact with our Service.</li>
            <li><strong>Device data:</strong> Device type, operating system, and browser version when you interact with the Service.</li>
            <li><strong>Cookies and local storage:</strong> We use a single first-party, HTTP-only session cookie required for authentication. This cookie keeps you logged in and is automatically removed when you log out or your session expires. We do not use advertising trackers, third-party analytics cookies, or cross-site tracking of any kind.</li>
            <li><strong>Analytics:</strong> We use Plausible Analytics to understand aggregate site traffic (pages visited, referral sources, country, device type). Plausible does not use cookies, does not collect personal data, and does not track visitors across sites or devices. All data is anonymized and aggregated. No cookie consent banner is required. See <a href="https://plausible.io/privacy" className="text-sage hover:underline" target="_blank" rel="noopener noreferrer">plausible.io/privacy</a> for details.</li>
          </ul>
          <p>
            <strong>Do Not Track:</strong> Some browsers offer a "Do Not Track" (DNT) setting.
            Because no uniform industry standard for DNT compliance exists, we do not alter our
            data practices in response to DNT signals. However, we do not engage in behavioral
            advertising or cross-site tracking regardless of your DNT setting.
          </p>
        </SubSection>

        <SubSection title="2.3 Information from third parties">
          <ul>
            <li><strong>OAuth providers:</strong> If you sign in via Google or Apple, we receive your name and email address from that provider.</li>
            <li><strong>Card delivery vendor:</strong> Thankster may provide delivery status for cards we send on your behalf.</li>
            <li><strong>Stripe:</strong> Stripe shares subscription status, payment success/failure events, and a customer ID with us via webhooks.</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="3. How We Use Your Information">
        <ul>
          <li>To create and manage your account.</li>
          <li>To print and mail physical cards to the recipients you specify, whether through scheduled occasions or on-demand sends (Send Now).</li>
          <li>To send SMS reminders about upcoming occasions (if you have consented).</li>
          <li>To send transactional emails: account confirmations, card delivery updates, payment receipts, and trial-expiration notices.</li>
          <li>To process subscription payments via Stripe and apply referral credits.</li>
          <li>To generate your Relationship Wrapped year-in-review (aggregated card stats).</li>
          <li>To detect and prevent fraud and abuse.</li>
          <li>To comply with legal obligations.</li>
          <li>If you have opted in, to send occasional marketing emails about MomentSent features or promotions. You may unsubscribe at any time.</li>
        </ul>
        <p>
          We do <strong>not</strong> sell your personal information, use it for behavioral advertising,
          or share it with data brokers.
        </p>
      </Section>

      <Section title="4. How We Share Your Information">
        <p>We share information only as necessary to operate the Service:</p>

        <SubSection title="Service providers (data processors)">
          <ul>
            <li><strong>Supabase:</strong> Hosts our database (PostgreSQL) and file storage. Data is stored in AWS US-East-1.</li>
            <li><strong>Stripe:</strong> Processes all subscription payments.</li>
            <li><strong>Thankster:</strong> Prints our folded greeting cards, stuffs them into envelopes, and mails them first-class to US and international addresses. Receives recipient name, mailing address, and card message.</li>
            <li><strong>Twilio:</strong> Delivers SMS reminders and delivery notifications to your phone number.</li>
            <li><strong>SendGrid:</strong> Delivers transactional and marketing emails.</li>
            <li><strong>Railway:</strong> Hosts our API server infrastructure.</li>
            <li><strong>Plausible Analytics:</strong> Provides privacy-first, cookieless website analytics. Plausible processes only anonymized, aggregate traffic data (page views, referral source, country, device type) and does not collect any personally identifiable information. Data is hosted in the EU on servers owned by Plausible Analytics OÜ.</li>
          </ul>
          <p>
            Each provider maintains its own data protection and privacy terms governing how
            they handle information we share with them. We select providers that contractually
            limit use of shared data to providing services on our behalf and do not use it
            for their own commercial purposes.
          </p>
        </SubSection>

        <SubSection title="Legal requirements">
          <p>
            We may disclose information if required by law, court order, or governmental authority,
            or if we believe in good faith that disclosure is necessary to protect the rights,
            property, or safety of MomentSent, our users, or the public.
          </p>
        </SubSection>

        <SubSection title="Business transfers">
          <p>
            If MomentSent is acquired, merges, or undergoes a similar transaction, your information
            may be transferred as part of that transaction. We will notify you via email before
            your data becomes subject to a different privacy policy.
          </p>
        </SubSection>
      </Section>

      <Section title="5. Recipient Data">
        <p>
          When you add a contact's mailing address, you represent that you have a legitimate
          personal relationship with that individual and that mailing a physical greeting card
          to them is appropriate within the context of that relationship. We do not use recipient
          addresses for any purpose other than delivering the cards you request.
        </p>
        <p>
          Recipient addresses are transmitted to Thankster solely for printing and mailing.
          Once a card is delivered, we retain the address in your account to make scheduling
          future cards convenient. You may delete a contact and their address at any time.
        </p>
        <p>
          Some recipients whose addresses you provide may be minors (for example, a child's
          birthday contact). Because recipient addresses are supplied by you, an adult account
          holder, and are not collected directly from recipients, this does not constitute the
          collection of personal information from children within the meaning of the Children's
          Online Privacy Protection Act (COPPA) or similar laws. Recipient addresses are used
          solely to mail physical cards on your behalf and are not used for any other purpose.
          By adding a minor recipient, you represent that you are the parent, legal guardian, or
          otherwise have the authority to send mail to that individual.
        </p>
      </Section>

      <Section title="6. SMS Communications">
        <p>
          By providing your phone number and checking the SMS consent box at signup, you consent
          to receive automated text messages from MomentSent for card reminders and delivery
          notifications. Message frequency depends on the number of occasions you track.
          Standard message and data rates may apply.
        </p>
        <p>
          To stop receiving SMS messages, reply <strong>STOP</strong> to any text. Reply{' '}
          <strong>HELP</strong> for assistance. You may also contact us at{' '}
          <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
            hello@momentsent.com
          </a>. We will honor your opt-out within 24 hours. We will not charge you for HELP or
          STOP messages. No purchase is necessary to opt out.
        </p>
        <p>
          We do <strong>not</strong> sell or share your mobile opt-in information, phone
          number, or SMS consent with any third parties or affiliates for their own
          marketing or promotional purposes. Your phone number is shared only with our
          SMS provider, Twilio, solely to deliver the text messages you have requested,
          and Twilio is contractually limited to using it for that purpose. No mobile
          information is shared with data brokers.
        </p>
      </Section>

      <Section title="7. Data Retention">
        <ul>
          <li><strong>Active accounts:</strong> Data is retained for the life of your account.</li>
          <li><strong>Closed accounts:</strong> We delete or anonymize personal data within 90 days of account closure, except where retention is required by law (e.g., financial records for 7 years).</li>
          <li><strong>Card content:</strong> Messages and card snapshots are retained to support your sending history and Wrapped feature. You may request deletion at any time.</li>
          <li><strong>Recipient portal tokens:</strong> Expire after 90 days and are purged thereafter.</li>
          <li><strong>Log data:</strong> Retained for up to 12 months for security and debugging purposes.</li>
          <li><strong>Aggregated / anonymized data:</strong> De-identified, aggregate statistics (such as total cards sent platform-wide) are not subject to this retention schedule and may be retained indefinitely. Such data cannot reasonably be used to identify any individual.</li>
        </ul>
      </Section>

      <Section title="8. Data Security">
        <p>
          We implement industry-standard security measures including TLS encryption in transit,
          AES-256 encryption at rest (via Supabase and Railway), row-level security policies
          on our database, and bcrypt-hashed passwords (managed by Supabase Auth). Access to
          production systems is restricted to authorized personnel.
        </p>
        <p>
          No method of transmission or storage is 100% secure. If we become aware of a security
          breach that affects your personal data, we will notify affected users without
          unreasonable delay and in accordance with applicable law, including the North Carolina
          Identity Theft Protection Act (N.C. Gen. Stat. § 75-65) and any other applicable
          state breach notification statutes.
        </p>
      </Section>

      <Section title="9. Your Rights and Choices">
        <p>Depending on your location, you may have the following rights:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
          <li><strong>Correction:</strong> Ask us to correct inaccurate or incomplete data.</li>
          <li><strong>Deletion:</strong> Request deletion of your personal data ("right to be forgotten").</li>
          <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format.</li>
          <li><strong>Objection / Restriction:</strong> Object to or restrict certain processing activities.</li>
          <li><strong>Opt-out of marketing:</strong> Unsubscribe from marketing emails at any time using the link in any email we send.</li>
        </ul>
        <p>
          To exercise any of these rights, email{' '}
          <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
            hello@momentsent.com
          </a>. We will respond within 45 days, or within the period required by applicable law.
          We may need to verify your identity before fulfilling certain requests.
        </p>

        <SubSection title="California residents (CCPA / CPRA)">
          <p>
            California residents have the right to know what personal information we collect,
            the right to delete it, the right to correct it, and the right to opt out of its
            sale or sharing. We do not sell or share personal information as defined by the CCPA
            and CPRA. To submit a verifiable consumer request, contact us at the email above.
          </p>
        </SubSection>

        <SubSection title="Virginia residents (VCDPA)">
          <p>
            Virginia residents have the right to access, correct, delete, and obtain a copy of
            their personal data, and to opt out of its sale and use for targeted advertising or
            profiling for decisions with legal or similarly significant effects. We do not sell
            personal data or use it for targeted advertising or such profiling. To exercise
            your rights, contact us at the email above. We will respond within 45 days.
          </p>
        </SubSection>
      </Section>

      <Section title="10. Children's Privacy">
        <p>
          The Service is not directed to children under 13. We do not knowingly collect personal
          information from children under 13. If we learn that we have collected such information,
          we will delete it promptly. If you believe we have collected information from a child
          under 13, please contact us at{' '}
          <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
            hello@momentsent.com
          </a>.
        </p>
      </Section>

      <Section title="11. International Users">
        <p>
          MomentSent is operated from the United States. If you access the Service from outside
          the US, your information may be transferred to, stored, and processed in the United
          States. By using the Service, you acknowledge this transfer. We rely on the published
          data protection terms and privacy commitments of our service providers as the transfer
          mechanism for any international data flows.
        </p>
      </Section>

      <Section title="12. Third-Party Links">
        <p>
          The Service may contain links to third-party websites (e.g., pricing comparison pages,
          partner card printers). We are not responsible for the privacy practices of those
          sites. We encourage you to review their privacy policies before providing any
          personal information.
        </p>
      </Section>

      <Section title="13. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. When we make material changes, we
          will notify you by email or by displaying a prominent notice in the app at least 14 days
          before the change takes effect. Continued use of the Service after the effective date
          constitutes your acceptance of the updated policy. The current effective date is shown
          at the top of this page.
        </p>
      </Section>

      <Section title="14. Contact Us">
        <p>
          MomentSent LLC<br />
          P.O. Box 342<br />
          Hubert, NC 28539<br />
          <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
            hello@momentsent.com
          </a>
        </p>
      </Section>

    </LegalShell>
  );
}

// ── Layout components ─────────────────────────────────────────────────────────

function LegalShell({
  title,
  effectiveDate,
  children,
}: {
  title: string;
  effectiveDate: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-cream">
      {/* Top bar */}
      <div className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="font-heading text-lg font-bold text-navy hover:opacity-80 transition-opacity">
            Moment<span className="text-sage">Sent</span>
          </Link>
          <Link to="/" className="font-body text-xs text-gray-400 hover:text-navy transition-colors">
            ← Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="font-heading text-4xl font-bold text-navy mb-2">{title}</h1>
        <p className="font-body text-sm text-gray-400 mb-10">
          Effective {effectiveDate} · MomentSent LLC · Hubert, NC
        </p>

        <div className="prose-legal space-y-10">
          {children}
        </div>

        {/* Footer links */}
        <div className="mt-16 pt-8 border-t border-gray-100 flex flex-wrap gap-4 text-xs font-body text-gray-400">
          <Link to="/privacy" className="hover:text-navy transition-colors">Privacy Policy</Link>
          <Link to="/terms"   className="hover:text-navy transition-colors">Terms of Service</Link>
          <a href="mailto:hello@momentsent.com" className="hover:text-navy transition-colors">
            Contact Support
          </a>
          <span className="ml-auto">© {new Date().getFullYear()} MomentSent LLC</span>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-heading text-xl font-bold text-navy mb-4 pb-2 border-b border-gray-100">
        {title}
      </h2>
      <div className="font-body text-sm text-gray-700 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="font-body text-sm font-semibold text-navy mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
